import type { EligibilityStatus } from './eligibility'
import type { ReservationStatus } from './reservation-status'

/**
 * RES-3 and Q-162 (the owner, 1 Nov): a second driver, entered at the counter
 * before the hand-over. The owner's answers:
 *
 * - one fee per rental, from the setting `rentals.second_driver_fee_cents`;
 *   none set, no second driver (a price is never guessed);
 * - the same eligibility rules as the renter, without the young-driver
 *   surcharge: `assessEligibility` itself, whose only age rule is a block
 *   (the surcharge is the quote's, and a second driver is never quoted);
 * - an ineligible one goes only on the Owner's override, with a reason;
 * - only the renter signs.
 *
 * And the lawyer's: the terms in force must allow a second driver
 * (`secondDriverAllowed`), since today's say only the signer drives.
 */

/** A bound against a slip of the keyboard, not a price: the fee itself is configuration. */
export const SECOND_DRIVER_FEE_MAX_CENTS = 100_000

export const SECOND_DRIVER_UNAVAILABLE = [
  /** The booking is not a confirmed one before its hand-over. */
  'not_before_handover',
  /** No terms in force, or terms that say only the signer drives. */
  'terms_forbid',
  /** `rentals.second_driver_fee_cents` is not set. */
  'fee_not_configured',
] as const
export type SecondDriverUnavailable = (typeof SECOND_DRIVER_UNAVAILABLE)[number]

export type SecondDriverAvailability =
  | { available: true; feeCents: number }
  | { available: false; reason: SecondDriverUnavailable }

/**
 * Whether the counter may add (or change) a second driver now, and at what fee.
 * Taking one off needs only the first condition (`not_before_handover`): a
 * driver added under other terms or another fee can always be taken off
 * before the car leaves.
 */
export function secondDriverAvailability(facts: {
  status: ReservationStatus
  /** The terms in force allow one; null when no terms are in force. */
  termsAllow: boolean | null
  feeCents: number | null
}): SecondDriverAvailability {
  if (facts.status !== 'confirmed') return { available: false, reason: 'not_before_handover' }
  if (facts.termsAllow !== true) return { available: false, reason: 'terms_forbid' }
  if (facts.feeCents === null) return { available: false, reason: 'fee_not_configured' }
  return { available: true, feeCents: facts.feeCents }
}

/** What the hand-over knows of the booking's live second driver. */
export interface SecondDriverHandover {
  /** Their identity recorded at the counter (decision 5, as for the renter). */
  identityRecorded: boolean
  /** Judged again at the hand-over's dates, from what is on file now. */
  eligibilityStatus: EligibilityStatus
  /** The Owner's reason was given when the driver was added. */
  overridden: boolean
  /** The birth date on their ID is not the one on file. */
  birthDateDiffers: boolean
}

/**
 * Why the second driver needs the Owner at the hand-over: ineligible at its
 * dates with no reason given when added, or a birth date on the ID that is
 * not the one judged.
 */
export function secondDriverNeedsOwner(driver: SecondDriverHandover): boolean {
  return (
    (driver.eligibilityStatus === 'ineligible' && !driver.overridden) ||
    (driver.identityRecorded && driver.birthDateDiffers)
  )
}
